import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import { DeleteOutlined, EditOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

import api from '../../services/api';
import { CadastroPage } from './CadastroPage';
import { listarConsultasV2, listarMatrizesV2, PAGE_SIZE } from '../../services/cadastrosV2';

const emptyConsultation = {
  idConsulta: undefined,
  idTipoCadastro: undefined,
  idConsultaContigencia: undefined,
  diasValidadeConsulta: undefined,
  reprovacaoAutomatica: false,
};

export function MatrizesV2() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [registrationTypes, setRegistrationTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(1), [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarMatrizesV2(debouncedSearch);
      setItems(response?.data || []);
    } catch (error) {
      setItems([]);
      message.error(error?.response?.data?.detail || 'Não foi possível carregar as matrizes.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const paginatedItems = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page]
  );

  const loadOptions = async () => {
    const [clientResponse, consultationResponse, typeResponse] = await Promise.all([
      api.get('/Cliente/clientesUsuario', { params: { page: 1, numeroRegistros: 1000 } }),
      listarConsultasV2(1, '', 1000),
      api.get('/TipoCadastro/list'),
    ]);
    setClients(clientResponse?.data?.items || []);
    setConsultations(consultationResponse?.data?.items || []);
    setRegistrationTypes(typeResponse?.data || []);
  };

  const openNew = async () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ consultas: [{ ...emptyConsultation }] });
    setOpen(true);
    setSaving(true);
    try {
      await loadOptions();
    } catch (error) {
      message.error('Não foi possível carregar os dados auxiliares da matriz.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async item => {
    setOpen(true);
    setSaving(true);
    try {
      const [detailResponse] = await Promise.all([
        api.get('/Matriz', { params: { idMatriz: item.id } }),
        loadOptions(),
      ]);
      const detail = detailResponse?.data;
      setEditing(detail);
      form.setFieldsValue({
        nome: detail.nome,
        idCliente: detail.idCliente,
        enderecoWebHookFinalizacao: detail.enderecoWebHookFinalizacao,
        enderecoWebHookStatus: detail.enderecoWebHookStatus,
        requestWebhookAuthorization: detail.requestWebhookAuthorization,
        consultas: (detail.consultas || []).map(consultation => ({
          id: consultation.id,
          idMatriz: detail.id,
          idConsulta: consultation.idConsulta,
          idTipoCadastro: consultation.idTipoCadastro,
          idConsultaContigencia: consultation.idConsultaContigencia,
          diasValidadeConsulta: consultation.diasValidadeConsulta,
          reprovacaoAutomatica: Boolean(consultation.reprovacaoAutomatica),
        })),
      });
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Não foi possível carregar os dados da matriz.');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const save = async values => {
    if (!values.consultas?.length) {
      message.error('Adicione ao menos uma consulta à matriz.');
      return;
    }
    setSaving(true);
    const payload = {
      id: editing?.id,
      nome: values.nome.trim(),
      idCliente: values.idCliente,
      enderecoWebHookFinalizacao: values.enderecoWebHookFinalizacao || null,
      enderecoWebHookStatus: values.enderecoWebHookStatus || null,
      requestWebhookAuthorization: values.requestWebhookAuthorization || null,
      consultas: values.consultas.map(consultation => ({
        id: consultation.id || 0,
        idMatriz: editing?.id || 0,
        idConsulta: consultation.idConsulta,
        idTipoCadastro: consultation.idTipoCadastro,
        idConsultaContigencia: consultation.idConsultaContigencia || null,
        diasValidadeConsulta: consultation.diasValidadeConsulta ?? null,
        reprovacaoAutomatica: Boolean(consultation.reprovacaoAutomatica),
      })),
    };

    try {
      if (editing) await api.put('/Matriz', payload);
      else await api.post('/Matriz', payload);
      message.success(`Matriz ${editing ? 'atualizada' : 'cadastrada'} com sucesso.`);
      close();
      await load();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Não foi possível salvar a matriz.');
    } finally {
      setSaving(false);
    }
  };

  const remove = item => {
    Modal.confirm({
      title: `Excluir a matriz “${item.nome}”?`,
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.delete(`/Matriz/${item.id}`);
          message.success('Matriz excluída com sucesso.');
          if (paginatedItems.length === 1 && page > 1) setPage(page - 1);
          await load();
        } catch (error) {
          message.error(error?.response?.data?.detail || 'Não foi possível excluir a matriz.');
        }
      },
    });
  };

  const consultationOptions = consultations.map(item => ({ label: item.nome, value: item.id }));
  const typeOptions = registrationTypes.map(item => ({ label: item.nome, value: item.id }));

  return (
    <>
      <CadastroPage
        title="Matrizes"
        newLabel="Nova matriz"
        search={search}
        onSearch={setSearch}
        loading={loading}
        items={paginatedItems}
        total={items.length}
        page={page}
        onPageChange={setPage}
        onNew={openNew}
        renderItem={item => (
          <List.Item
            key={item.id}
            actions={[
              <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => openEdit(item)}>Editar</Button>,
              <Button key="delete" type="link" danger icon={<DeleteOutlined />} onClick={() => remove(item)}>Excluir</Button>,
            ]}
          >
            <List.Item.Meta
              title={item.nome}
              description={`ID: ${item.id} • Cliente: ${item?.cliente
                ?.nomeFantasia || item?.cliente?.razaoSocial || 'Não informado'}`}
            />
          </List.Item>
        )}
      />

      <Modal
        open={open}
        title={editing ? `Editar matriz #${editing.id}` : 'Nova matriz'}
        footer={null}
        width={1120}
        maskClosable={false}
        destroyOnHidden
        onCancel={close}
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Informe o nome.' }]}>
                <Input maxLength={150} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="idCliente" label="Cliente" rules={[{ required: true, message: 'Selecione o cliente.' }]}>
                <Select showSearch optionFilterProp="label" options={clients.map(item => ({ label: item.nomeFantasia, value: item.id }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="enderecoWebHookFinalizacao" label="Webhook de finalização" rules={[{ type: 'url', warningOnly: true, message: 'Informe uma URL válida.' }]}>
                <Input placeholder="https://..." maxLength={500} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="enderecoWebHookStatus" label="Webhook de status" rules={[{ type: 'url', warningOnly: true, message: 'Informe uma URL válida.' }]}>
                <Input placeholder="https://..." maxLength={500} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="requestWebhookAuthorization" label="Authorization dos webhooks">
            <Input.Password placeholder="Token ou cabeçalho de autorização" maxLength={1000} />
          </Form.Item>

          <Form.List name="consultas">
            {(fields, { add, remove: removeConsultation }) => (
              <>
                {fields.map(field => (
                  <div className="cadastros-v2-consulta-row" key={field.key}>
                    <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                    <Form.Item name={[field.name, 'idMatriz']} hidden><Input /></Form.Item>
                    <Row gutter={12} align="middle">
                      <Col xs={24} lg={6}>
                        <Form.Item name={[field.name, 'idConsulta']} label="Consulta" rules={[{ required: true, message: 'Selecione.' }]}>
                          <Select showSearch optionFilterProp="label" options={consultationOptions} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={5}>
                        <Form.Item name={[field.name, 'idTipoCadastro']} label="Tipo de cadastro" rules={[{ required: true, message: 'Selecione.' }]}>
                          <Select showSearch optionFilterProp="label" options={typeOptions} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={5}>
                        <Form.Item name={[field.name, 'idConsultaContigencia']} label="Contingência">
                          <Select allowClear showSearch optionFilterProp="label" options={consultationOptions} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} lg={3}>
                        <Form.Item name={[field.name, 'diasValidadeConsulta']} label="Validade (dias)">
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={8} lg={3}>
                        <Form.Item name={[field.name, 'reprovacaoAutomatica']} label="Reprovação auto." valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col xs={4} lg={2}>
                        <Button danger type="text" aria-label="Remover consulta" icon={<MinusCircleOutlined />} onClick={() => removeConsultation(field.name)} />
                      </Col>
                    </Row>
                  </div>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ ...emptyConsultation })}>
                  Adicionar consulta
                </Button>
              </>
            )}
          </Form.List>

          <div className="cadastros-v2-modal-actions">
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Salvar</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
